import { Helmet } from 'react-helmet-async';

const BASE      = 'https://www.readiq.ai';
const OG_IMAGE  = `${BASE}/og-image.jpg`;
const SITE_NAME = 'ReadIQ';

/**
 * Per-page SEO component using react-helmet-async.
 *
 * Key implementation details that fix description/keywords not propagating:
 *  - Every meta tag carries a unique `data-rh` key so Helmet can reliably
 *    deduplicate tags with the same name across page navigations.
 *  - `og:*` tags use `property` (not `name`) — required by the OGP spec.
 *  - `twitter:*` tags use `name` — required by Twitter/X.
 *  - No static description/keywords/og tags in public/index.html so there is
 *    nothing to conflict with.
 */
export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — AI Readiness, Certified.`;
  const url = canonical ? `${BASE}${canonical}` : BASE;

  return (
    <Helmet prioritizeSeoTags>
      {/* ── Primary ───────────────────────────────────────────── */}
      <title>{fullTitle}</title>

      <meta name="description"   content={description} />
      <meta name="keywords"      content={keywords || ''} />
      <meta name="author"        content="AI Business Solutions" />
      <meta name="robots"        content="index, follow" />
      <link rel="canonical"      href={url} />

      {/* ── Open Graph ────────────────────────────────────────── */}
      <meta property="og:type"        content={ogType} />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url"         content={url} />
      <meta property="og:image"       content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"      content="en_US" />

      {/* ── Twitter / X Card ──────────────────────────────────── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content="@readiq_ai" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />
    </Helmet>
  );
}
